import { useState } from 'react';
import { ChevronLeft, Eye, EyeOff, Plus } from 'lucide-react';
import PhoneInput from 'react-phone-number-input';
import en from 'react-phone-number-input/locale/en.json';
import 'react-phone-number-input/style.css';
import { toast } from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout';
import { useAuth } from '../hooks/useAuth';
import { createManagedAgent } from '../services/api';

const PASSWORD_MIN = 8;
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
const USERNAME_REGEX = /^[a-z][a-z0-9_]*$/;
const NAME_MAX_LENGTH = 100;
const USERNAME_MAX_LENGTH = 50;

const hasValidCostPercent = (value: string) => {
  if (value.trim() === '') return false;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 && n <= 100;
};

const normalizeUsername = (value: string) => value.toLowerCase().replace(/[^a-z0-9_]/g, '');

const toRate = (percent: string) => {
  const n = Number(percent);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(1, Number((n / 100).toFixed(4)));
};

const toPercent = (rate: number | string | undefined) => {
  const n = Number(rate ?? 0);
  if (!Number.isFinite(n)) return '0.00';
  return (n * 100).toFixed(2);
};

export default function CreateManagedAgentPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    name: '',
    username: '',
    email: '',
    phone: '',
    password: '',
    costPercent: '',
    canCreateShopPopup: false,
    canManageShopPopupPermission: false,
    defaultShopCanCreatePlayerPopup: false,
  });

  const onCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    const usernameNorm = normalizeUsername(form.username).trim();
    const nameTrimmed = form.name.trim();
    if (!nameTrimmed) {
      toast.error('Name is required');
      return;
    }
    if (nameTrimmed.length > NAME_MAX_LENGTH) {
      toast.error(`Name must be at most ${NAME_MAX_LENGTH} characters`);
      return;
    }
    if (usernameNorm.length > USERNAME_MAX_LENGTH) {
      toast.error(`Username must be at most ${USERNAME_MAX_LENGTH} characters`);
      return;
    }
    if (!USERNAME_REGEX.test(usernameNorm)) {
      toast.error('Username must start with a letter and contain only lowercase letters, numbers, and "_"');
      return;
    }
    if (!PASSWORD_REGEX.test(form.password)) {
      toast.error('Password must include uppercase, lowercase, number and be at least 8 characters');
      return;
    }
    if (!hasValidCostPercent(form.costPercent)) {
      toast.error('Please enter a valid cost rate from 0 to 100');
      return;
    }
    setSubmitting(true);
    try {
      const res = await createManagedAgent({
        name: nameTrimmed,
        username: usernameNorm,
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        password: form.password,
        cost_rate: toRate(form.costPercent),
        can_create_shop_popup: form.canCreateShopPopup,
        can_manage_shop_popup_permission: form.canManageShopPopupPermission,
        default_shop_can_create_player_popup: form.defaultShopCanCreatePlayerPopup,
      });
      if (!res.success) {
        toast.error(res.message || 'Could not create agent');
        return;
      }
      toast.success('Agent created');
      navigate('/agents', { replace: true });
    } catch {
      toast.error('Could not create agent');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppLayout contentFullWidth>
      <div className="w-full">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <Link
              to="/agents"
              className="inline-flex items-center gap-1 text-sm font-semibold text-orange-700 hover:text-orange-900"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
              Back to Agents
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">Create agent</h1>
            <p className="text-sm text-gray-600">Create a new agent account and assign cost rate.</p>
          </div>
        </div>

        <section className="rounded-xl border border-orange-100 bg-white p-5 shadow-sm sm:p-6">
          <form onSubmit={onCreate} autoComplete="off" className="space-y-5">
            <div className="grid items-start gap-4 md:grid-cols-2">
              <input
                className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-base"
                placeholder="Name"
                required
                maxLength={NAME_MAX_LENGTH}
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
              <div>
                <input
                  autoComplete="off"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-base"
                  placeholder="Username"
                  required
                  maxLength={USERNAME_MAX_LENGTH}
                  value={form.username}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      username: normalizeUsername(e.target.value).slice(0, USERNAME_MAX_LENGTH),
                    }))
                  }
                />
                <p className="mt-1 text-xs text-gray-500">
                  Lowercase letters, numbers, and <span className="font-medium">_</span>; must start with a letter.
                </p>
              </div>
              <input
                autoComplete="off"
                className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-base"
                type="email"
                placeholder="Email"
                required
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              />
              <div className="agent-phone-field">
                <PhoneInput
                  international
                  defaultCountry="US"
                  countryCallingCodeEditable={false}
                  labels={en}
                  placeholder="Phone"
                  value={form.phone || undefined}
                  onChange={(value) => setForm((p) => ({ ...p, phone: value ?? '' }))}
                  className="PhoneInput"
                />
              </div>
              <div>
                <div className="relative">
                  <input
                    autoComplete="new-password"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2.5 pr-11 text-base"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    required
                    value={form.password}
                    onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                  />
                  <button
                    type="button"
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-md p-2 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4.5 w-4.5" aria-hidden /> : <Eye className="h-4.5 w-4.5" aria-hidden />}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Password must have uppercase, lowercase, number, and at least {PASSWORD_MIN} characters.
                </p>
              </div>

              <div>
                <div className="flex items-center gap-2">
                  <input
                    className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-base"
                    type="number"
                    min={0}
                    max={100}
                    step="0.01"
                    placeholder="Cost rate (%)"
                    value={form.costPercent}
                    onChange={(e) => setForm((p) => ({ ...p, costPercent: e.target.value }))}
                  />
                  <span className="shrink-0 text-sm font-medium text-gray-500">%</span>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Must be <span className="font-medium">≥ Super Agent cost rate</span>
                  {typeof user?.costRate === 'number' ? ` (${toPercent(user.costRate)}%)` : ''}.
                </p>
              </div>

              {user?.can_delegate_popups ? (
              <div className="md:col-span-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="text-sm font-medium text-gray-900">In-app popups / broadcasts (delegated)</p>
                <label className="mt-3 flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={form.canCreateShopPopup}
                    onChange={(e) => setForm((p) => ({ ...p, canCreateShopPopup: e.target.checked }))}
                  />
                  <span className="text-sm text-gray-800">
                    <span className="font-medium">Allow this agent to send popups/broadcasts to shops/players</span>
                    <span className="mt-0.5 block text-xs text-gray-500">
                      When enabled, the agent can send delegated popups/broadcasts.
                    </span>
                  </span>
                </label>
                <label className="mt-3 flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={form.canManageShopPopupPermission}
                    onChange={(e) => setForm((p) => ({ ...p, canManageShopPopupPermission: e.target.checked }))}
                  />
                  <span className="text-sm text-gray-800">
                    <span className="font-medium">Allow this agent to manage popup permission for its shops</span>
                    <span className="mt-0.5 block text-xs text-gray-500">
                      Agent can enable/disable shop popup permission per shop.
                    </span>
                  </span>
                </label>
                <label className="mt-3 flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={form.defaultShopCanCreatePlayerPopup}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        defaultShopCanCreatePlayerPopup: e.target.checked,
                      }))
                    }
                  />
                  <span className="text-sm text-gray-800">
                    <span className="font-medium">Default: new shops can send popups/broadcasts to players</span>
                    <span className="mt-0.5 block text-xs text-gray-500">
                      When the agent creates a shop, that shop can inherit this default. You can override per shop
                      later.
                    </span>
                  </span>
                </label>
              </div>
              ) : null}
            </div>

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Link
                to="/agents"
                className={`rounded-lg border border-gray-200 px-4 py-2 text-center text-sm font-medium text-gray-700 hover:bg-gray-50 ${
                  submitting ? 'pointer-events-none opacity-60' : ''
                }`}
                aria-disabled={submitting}
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={submitting || !PASSWORD_REGEX.test(form.password) || !hasValidCostPercent(form.costPercent)}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-60"
              >
                <Plus className="h-4 w-4" aria-hidden />
                {submitting ? 'Creating...' : 'Create agent'}
              </button>
            </div>
          </form>
        </section>
      </div>
    </AppLayout>
  );
}

